// AIMS PWA - "Take offline" orchestration (AIMS-Offline-Inspection.md §2/§9 Ph2).
//
// Downloads everything a visit needs to be inspected with no signal and caches
// it ATOMICALLY: the asset pool, every needed checklist template, and the
// item→auditType map are written in one Dexie transaction so an interrupted
// download never leaves a half-cached, un-inspectable visit. Only after the
// transaction commits AND a verify pass confirms every asset + template is
// present do we stamp syncMeta.ready = true.
//
// Reports progress via an onProgress({ phase, done, total }) callback so the UI
// can show "everything cached ✓". Storage estimate is exposed separately.

import db from './db';
import { offlineCheckout, fetchChecklistTemplate, fetchTypeMap, offlineRelease } from './offlineApi';

// Best-effort storage budget estimate (bytes). Not all browsers implement it
// (iOS Safari often returns undefined) - callers treat null as "unknown".
export async function getStorageEstimate() {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const { usage, quota } = await navigator.storage.estimate();
      return { usage: usage ?? null, quota: quota ?? null };
    }
  } catch (_) { /* ignore */ }
  return { usage: null, quota: null };
}

// Ask the browser to resist evicting our data (iOS hardening, §7). Harmless if
// unsupported/denied.
export async function requestPersistence() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      return await navigator.storage.persist();
    }
  } catch (_) { /* ignore */ }
  return false;
}

const norm = (s) => (s == null ? '' : String(s));

// Take a visit offline. Returns { ok, cachedAssets, cachedTemplates } or throws.
export async function takeVisitOffline(visitId, { deviceLabel, onProgress } = {}) {
  const report = (phase, done, total) => { if (onProgress) onProgress({ phase, done, total }); };

  report('checkout', 0, 1);
  await requestPersistence();

  // 1) Lease + fetch the pool (also gives us the audit types to cache).
  const checkout = await offlineCheckout(visitId, deviceLabel);
  const assets = Array.isArray(checkout.assets) ? checkout.assets : [];
  const auditTypes = Array.isArray(checkout.auditTypesNeeded) ? checkout.auditTypesNeeded : [];
  report('checkout', 1, 1);

  // 2) Fetch every needed checklist template + the type map BEFORE writing, so
  //    the Dexie transaction is a single atomic commit (no network inside it).
  report('templates', 0, auditTypes.length);
  const templates = [];
  for (let i = 0; i < auditTypes.length; i++) {
    const at = auditTypes[i];
    // eslint-disable-next-line no-await-in-loop
    const items = await fetchChecklistTemplate(at);
    templates.push({ auditType: at, items, cachedAt: Date.now() });
    report('templates', i + 1, auditTypes.length);
  }

  report('typeMap', 0, 1);
  let typeMapRows = [];
  try { typeMapRows = await fetchTypeMap(); } catch (_) { typeMapRows = []; }
  report('typeMap', 1, 1);

  // 3) Atomic write: assets + templates + typeMap + syncMeta in one transaction.
  report('caching', 0, 1);
  await db.transaction('rw', db.assets, db.checklistTemplates, db.typeMap, db.syncMeta, async () => {
    // Replace this visit's asset rows (fresh pool wins).
    await db.assets.where('visitId').equals(visitId).delete();
    if (assets.length) {
      await db.assets.bulkPut(assets.map(a => ({ ...a, visitId, catNo: norm(a.catNo), cachedAt: Date.now() })));
    }
    // Templates are shared across visits - upsert by auditType.
    if (templates.length) await db.checklistTemplates.bulkPut(templates);
    // Type map is global - refresh wholesale.
    await db.typeMap.clear();
    if (typeMapRows.length) {
      await db.typeMap.bulkAdd(typeMapRows.map(r => ({ item: r.item, auditType: r.auditType })));
    }
    // Mark NOT ready yet - verify runs after the tx commits.
    await db.syncMeta.put({
      visitId,
      ready: false,
      company: checkout.visit && checkout.visit.company,
      checkoutId: checkout.checkoutId || null,
      assetCount: assets.length,
      templateCount: templates.length,
      lastSyncAt: Date.now(),
      leasedAt: Date.now(),
    });
  });
  report('caching', 1, 1);

  // 4) Verify pass - every asset present + every audit type has a template row.
  report('verify', 0, 1);
  const cachedAssetCount = await db.assets.where('visitId').equals(visitId).count();
  const missingTemplates = [];
  for (const at of auditTypes) {
    // eslint-disable-next-line no-await-in-loop
    const t = await db.checklistTemplates.get(at);
    if (!t) missingTemplates.push(at);
  }
  const ok = cachedAssetCount === assets.length && missingTemplates.length === 0;
  await db.syncMeta.update(visitId, { ready: ok, verifiedAt: Date.now() });
  report('verify', 1, 1);

  if (!ok) {
    const err = new Error('Verification failed after caching. Some assets or checklists did not save. Please retry with a connection.');
    err.detail = { cachedAssetCount, expected: assets.length, missingTemplates };
    throw err;
  }

  return { ok: true, cachedAssets: cachedAssetCount, cachedTemplates: templates.length };
}

// Is this visit currently cached + verified-ready offline?
export async function isVisitOffline(visitId) {
  try {
    const meta = await db.syncMeta.get(visitId);
    return !!(meta && meta.ready);
  } catch (_) {
    return false;
  }
}

// Count of unsynced (pending upload) inspections for one visit.
export async function pendingCountForVisit(visitId) {
  try {
    return await db.outbox.filter((o) => o.visitId === visitId && !o.syncedAt).count();
  } catch (_) {
    return 0;
  }
}

// Release a visit's offline copy: clears the local cached pool + meta + this
// visit's already-synced outbox rows, and releases the server lease (if online).
// REFUSES by default when unsynced inspections exist (would lose field work) -
// pass { force: true } to remove anyway. Pass { localOnly: true } to skip the
// server release call (used when the lease was already cleared at HQ; calling
// offline-release then would close teammates' newer checkout rows). Shared
// checklist templates / typeMap are left in place (other offline visits may
// need them; harmless to keep).
// Returns { released: true } or { blocked: true, pending: n }.
export async function releaseVisitOffline(visitId, { force = false, localOnly = false } = {}) {
  const pending = await pendingCountForVisit(visitId);
  if (pending > 0 && !force) return { blocked: true, pending };

  // Best-effort server lease release (only possible online); ignore failures so
  // the local copy can always be cleared even with no signal.
  if (!localOnly && navigator.onLine) {
    try { await offlineRelease(visitId); } catch (_) { /* lease clears on next sync/checkout */ }
  }

  await db.transaction('rw', db.assets, db.outbox, db.syncMeta, async () => {
    await db.assets.where('visitId').equals(visitId).delete();
    const syncedKeys = await db.outbox
      .filter((o) => o.visitId === visitId && !!o.syncedAt)
      .primaryKeys();
    if (syncedKeys.length) await db.outbox.bulkDelete(syncedKeys);
    if (force) {
      const pendKeys = await db.outbox
        .filter((o) => o.visitId === visitId && !o.syncedAt)
        .primaryKeys();
      if (pendKeys.length) await db.outbox.bulkDelete(pendKeys);
    }
    await db.syncMeta.delete(visitId);
  });
  return { released: true };
}

// All visits currently taken offline (verified-ready), newest lease first, each
// with a live pending-upload count - drives the offline landing/picker (§8).
export async function listOfflineVisits() {
  try {
    const metas = await db.syncMeta.toArray();
    const ready = metas.filter((m) => m.ready);
    const out = [];
    for (const m of ready) {
      // eslint-disable-next-line no-await-in-loop
      const pending = await db.outbox.filter((o) => o.visitId === m.visitId && !o.syncedAt).count();
      out.push({
        visitId: m.visitId,
        company: m.company || '',
        assetCount: m.assetCount || 0,
        pendingCount: pending,
        lastSyncAt: m.lastSyncAt || null,
        leasedAt: m.leasedAt || null,
      });
    }
    return out.sort((a, b) => (b.leasedAt || 0) - (a.leasedAt || 0));
  } catch (_) {
    return [];
  }
}
